/**
 * AssetCard — single tile in the gallery grid.
 *
 * Extracted from BrandAssetGallery.jsx during May 2026 refactor.
 */
import React, { useState } from "react";
import { Badge } from "../ui/badge";
import { CheckCircle2, Circle, ImageOff, Camera } from "lucide-react";
import { verdictMeta, shortFolder } from "./utils";

function AssetCard({ asset, selected, onSelect, onOpen }) {
  const [broken, setBroken] = useState(false);
  const verdict = asset.classifier_verdict;
  const vMeta = verdictMeta(verdict);
  return (
    <div
      className={`relative group rounded-lg border bg-white overflow-hidden transition ${
        selected ? "ring-2 ring-blue-500 border-blue-500" : "border-slate-200 hover:border-slate-300"
      }`}
      data-testid={`gallery-card-${asset.asset_id}`}
    >
      {/* Phase 1.4 — verdict colored bar across the top of the card.
          Width: full. Height: 3px. Click target same as the card open
          button so accidental misclicks don't change state. */}
      <div
        className={`h-[3px] w-full ${vMeta.color}`}
        title={`Classifier: ${vMeta.label}`}
        data-testid={`gallery-card-verdict-bar-${asset.asset_id}`}
        data-verdict={verdict || "unclassified"}
      />
      <button
        type="button"
        onClick={() => onOpen(asset)}
        className="block w-full aspect-square bg-slate-100 cursor-pointer"
        data-testid={`gallery-card-open-${asset.asset_id}`}
      >
        {broken || !asset.thumbnail_url ? (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <ImageOff className="h-8 w-8" />
          </div>
        ) : (
          <img
            src={asset.thumbnail_url}
            alt={asset.file_name || asset.asset_id}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setBroken(true)}
          />
        )}
      </button>

      {/* Status pill */}
      <div className="absolute top-2 left-2 flex flex-wrap gap-1">
        {asset.is_active ? (
          <Badge className="bg-emerald-500 text-white border-0 text-[10px]">Active</Badge>
        ) : (
          <Badge className="bg-slate-500 text-white border-0 text-[10px]">Inactive</Badge>
        )}
        <Badge variant="outline" className="bg-white/90 text-[10px]">
          {asset.origin}
        </Badge>
        {/* Phase 1.3 — Stock source pill. Surfaces Unsplash/Pexels origin at
            a glance in the gallery. Brand-coloured pill that's visually
            subordinate to the verdict badge. */}
        {asset.origin === "stock" && asset.asset_metadata?.source ? (
          <Badge
            className={
              asset.asset_metadata.source === "unsplash"
                ? "bg-black text-white border-0 text-[10px]"
                : "bg-teal-600 text-white border-0 text-[10px]"
            }
            data-testid={`gallery-card-stock-source-${asset.asset_id}`}
          >
            <Camera className="h-2.5 w-2.5 mr-1" />
            {asset.asset_metadata.source === "unsplash" ? "Unsplash" : "Pexels"}
          </Badge>
        ) : null}
        {verdict ? (
          <Badge
            className={`${vMeta.color} text-white border-0 text-[10px]`}
            data-testid={`gallery-card-verdict-badge-${asset.asset_id}`}
          >
            {vMeta.label}
          </Badge>
        ) : null}
      </div>

      {/* Select checkbox */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(asset.asset_id);
        }}
        className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow hover:bg-white"
        data-testid={`gallery-card-select-${asset.asset_id}`}
        aria-label={selected ? "Deselect" : "Select"}
      >
        {selected ? (
          <CheckCircle2 className="h-5 w-5 text-blue-600" />
        ) : (
          <Circle className="h-5 w-5 text-slate-400" />
        )}
      </button>

      <div className="p-2 text-xs space-y-0.5">
        <div className="truncate font-medium" title={asset.file_name}>
          {asset.file_name || asset.asset_id.slice(0, 8)}
        </div>
        <div className="truncate text-slate-500" title={asset.source_folder}>
          {shortFolder(asset.source_folder)}
        </div>
      </div>
    </div>
  );
}


export default AssetCard;
export { AssetCard };
